import { useEffect, useRef } from "react";

/**
 * Attaches an IntersectionObserver to a sentinel element.
 * Calls `callback` whenever the sentinel enters the viewport.
 *
 * @param {Function} callback  - Called when the sentinel is visible
 * @param {string}  rootMargin - How far before the sentinel to trigger (default: 300px)
 * @returns {React.RefObject}  - Attach to the sentinel DOM element
 */
export function useScrollObserver(callback, { rootMargin = "300px", threshold = 0 } = {}) {
  const ref = useRef(null);
  // Always call the latest callback without re-binding the observer
  const cbRef = useRef(callback);
  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) cbRef.current();
      },
      { rootMargin, threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
    // Re-bind if layout options change (rare)
  }, [rootMargin, threshold]);

  return ref;
}
